import { useQuery } from '@apollo/client'
import { MyTrainingsCatalogFileViewerDocument } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'

import { FileViewer, File as FileViewerFile } from 'modules/trainings/components/Files/FileViewer'

interface TrainingFile extends FileViewerFile {}

export default function CatalogFileViewer() {
  const { trainingId, fileId, fileType } = useStrictParams<'trainingId' | 'fileId' | 'fileType'>()
  const { navigation } = useNavigation()

  const { data, loading, error } = useQuery(MyTrainingsCatalogFileViewerDocument, {
    variables: {
      trainingId: String(trainingId),
    },
  })

  if (loading) return null
  if (error) throw error

  const trainingFiles = data?.trainings?.trainingFilesConnection?.nodes || []
  const trainingClassFiles = data?.trainings?.trainingClassFilesConnection?.nodes || []

  const files: TrainingFile[] = []
  for (const file of trainingFiles) {
    files.push({
      id: file.id,
      filename: file.filename,
      title: file.title,
      contentType: file.contentType,
      url: file.url,
      type: 'training' as const,
    })
  }
  for (const file of trainingClassFiles) {
    files.push({
      id: file.id,
      filename: file.filename,
      title: file.title,
      contentType: file.contentType,
      url: file.url,
      type: 'trainingClass' as const,
    })
  }

  // Sort files by title (fallback to filename)
  files.sort((a, b) => ((a.title || a.filename) > (b.title || b.filename) ? 1 : -1))

  const currentFile = files.find((f) => f.id === String(fileId) && f.type === fileType)
  if (!currentFile) {
    throw new Error(`File with ID ${fileId} not found in training ${trainingId}.`)
  }

  return (
    <FileViewer
      file={currentFile}
      files={files}
      urlBackTo={navigation.myTrainings.catalog.detail.materials.fullPath({
        trainingId: trainingId || '',
      })}
      goNextFile={(prevFileInViewer: TrainingFile): string => {
        if (prevFileInViewer && prevFileInViewer.id) {
          return navigation.myTrainings.catalog.detail.materials.previewFile.fullPath({
            trainingId: trainingId || '',
            fileId: String(prevFileInViewer.id),
            fileType: prevFileInViewer.type,
          })
        }
        return '#'
      }}
      goPrevFile={(nextFileInViewer: TrainingFile): string => {
        if (nextFileInViewer && nextFileInViewer.id) {
          return navigation.myTrainings.catalog.detail.materials.previewFile.fullPath({
            trainingId: trainingId || '',
            fileId: String(nextFileInViewer.id),
            fileType: nextFileInViewer.type,
          })
        }
        return '#'
      }}
    />
  )
}
